import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import styles from './LazyImage.module.css';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  aspectRatio?: string;
}

/**
 * Lazy-loading image component with skeleton placeholder
 * Uses IntersectionObserver for efficient lazy loading
 */
export function LazyImage({ src, alt, className, aspectRatio = '2 / 1' }: LazyImageProps) {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isInView, setIsInView] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const imgElement = imgRef.current;
    if (!imgElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '50px' }
    );

    observer.observe(imgElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  return (
    <div
      ref={imgRef}
      className={clsx(styles.container, className)}
      style={{ aspectRatio }}
    >
      {isInView && (
        <img
          src={src}
          alt={alt}
          className={clsx(styles.image, isLoaded && styles.loaded)}
          onLoad={handleLoad}
        />
      )}
      <div className={clsx(styles.placeholder, isLoaded && styles.loaded)} />
    </div>
  );
}
